import { Notification } from '@domain/notification'
import { QuotaErrorCode } from '@domain/quota-errors'

import useModalsStore from '@infrastructure/stores/modals/modals-store'

import { TrackingManager } from '../tracking/tracking-manager'

export type MessageType = keyof ReturnType<typeof buildMessages>

export type Message = Omit<Notification, 'id'>

export const buildQuotaMessages = (
  env: ImportMetaEnv,
  trackingManager: TrackingManager,
): Record<QuotaErrorCode, Message> => ({
  'error-hit-limit-anonymous': {
    level: 'fire',
    title: 'You’ve reached your daily quota.',
    message: (
      <>
        Thanks for using Writefull!
        <br />
        Log in or create an account now to continue using our tools.
      </>
    ),
    cta: {
      label: 'Log in',
      action: () => useModalsStore.getState().openSignIn(),
    },
    secondaryCta: {
      label: 'Create an account',
      action: () => {
        const url = `${env.VITE_WF_CUSTOMER_PORTAL_URL}/signup`
        window.open(url, '_blank', 'noopener noreferrer')
        trackingManager.track('create-account', 'clicked', { source: 'rate-limit-message' })
      },
    },
  },
  'error-hit-limit-freemium': {
    level: 'fire',
    title: 'You’re on fire!',
    message: (
      <>
        You’ve hit your Writefull quota.
        <br />
        Become Premium now to continue using Writefull!
      </>
    ),
    cta: {
      label: '🏆  Get Writefull Premium',
      action: () => {
        const url = `${env.VITE_WF_CUSTOMER_PORTAL_URL}/signup`
        window.open(url, '_blank', 'noopener noreferrer')
        trackingManager.track('upgrade', 'clicked', { source: 'rate-limit-message' })
      },
    },
  },
  'error-hit-limit-premium': {
    level: 'warning',
    title: 'Premium daily quota reached',
    message: (
      <p>
        Sorry, you&apos;ve reached your daily quota.
        <br />
        We set this quota to avoid abuse. The Premium quota is high enough to process an average PhD
        thesis a day.
        <br />
        <br />
        If you think you shouldn&apos;t have hit this quota yet and this is an error, please let us
        know at&nbsp;
        <a href='mailto:support@writefull.com' style={{ color: '#0ea5e9' }}>
          support@writefull.com
        </a>
        .
      </p>
    ),
  },
})

export const buildMessages = (
  env: ImportMetaEnv,
  trackingManager: TrackingManager,
): Record<keyof typeof messages, Message> => {
  const messages = {
    default: {
      title: 'Error',
      message:
        'Something went wrong. Please try again later. If this keeps happening, contact us at support@writefull.com.',
      level: 'error',
    },
    'error-generating-ai-empty-body': {
      title: 'Unable to generate',
      message: 'Please enter a text before clicking to generate.',
      level: 'warning',
    },
    ...buildQuotaMessages(env, trackingManager),
  } as const

  return messages
}
